import { Dialog, PageHeader } from "@/components/ui/design-system";
import { Select as DesignSelect } from "@/components/ui/design-system";
import { useCallback, useEffect, useId, useState, type FormEvent } from "react";
import { Navigate } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { UserMfaSettings } from "@/components/UserMfaSettings";
import { Button } from "@/components/ui/button";
import { SkeletonTablica } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import { porukaDohvata, procitajGreske } from "@/lib/greske";
import {
  arhivirajKorisnika,
  dohvatiKorisnike,
  dohvatiReference,
  obrisiKorisnika,
  dohvatiRadnikeZaPovezivanje,
  dohvatiUloge,
  izmijeniKorisnika,
  postaviAktivnost,
  resetirajLozinku,
  stvoriKorisnika,
  type Korisnik,
  type PrivremenaLozinka,
  type Reference,
  type RadnikZaPovezivanje,
  type Uloga,
} from "@/lib/korisnici";

const POLJE =
  "h-[var(--spacing-control)] rounded-[var(--radius-control)] border border-[var(--color-input-border)] bg-white px-3";
const KARTICA =
  "rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)] p-4 sm:p-6";

function zadnjaPrijava(iso: string | null): string {
  if (!iso) return "Nikad";
  return new Intl.DateTimeFormat("hr-HR", {
    dateStyle: "short",
    timeStyle: "short",
    timeZone: "Europe/Zagreb",
  }).format(new Date(iso));
}

function porukaSpremanja(error: unknown): string {
  const { polja, opcenito } = procitajGreske(error);
  return [...Object.values(polja), ...opcenito].join(" ");
}

interface Obrazac {
  /** Null while adding a new account. */
  korisnik: Korisnik | null;
  full_name: string;
  telefon: string;
  email: string;
  uloge: string[];
  radnik_id: string;
}

function prazanObrazac(): Obrazac {
  return { korisnik: null, full_name: "", telefon: "", email: "", uloge: [], radnik_id: "" };
}

function obrazacZa(korisnik: Korisnik): Obrazac {
  return {
    korisnik,
    full_name: korisnik.full_name,
    telefon: korisnik.telefon ?? "",
    email: korisnik.email,
    uloge: korisnik.uloge.map((uloga) => uloga.code),
    radnik_id: korisnik.radnik?.id ?? "",
  };
}

/**
 * Delete or archive, never both.
 *
 * An account that has never touched anything can go outright; one that has is
 * the name on entries that must keep saying who made them, so it is closed and
 * kept. The count says which case this is, and the tooltip says why the row
 * offers what it offers rather than leaving the administrator to guess.
 */
function RadnjaRacuna({
  korisnik,
  reference,
  smije,
  radi,
  naBrisanje,
  naArhiviranje,
}: {
  korisnik: Korisnik;
  reference: Reference | undefined;
  smije: boolean;
  radi: boolean;
  naBrisanje: () => void;
  naArhiviranje: () => void;
}) {
  // Until the count arrives, neither button: offering "Obriši" on a guess is
  // how an account with history gets a confusing refusal instead of a choice.
  if (!smije || !reference) return null;
  if (reference.ukupno === 0) {
    return (
      <Button size="sm" variant="destructive" disabled={radi} onClick={naBrisanje}>
        Obriši
      </Button>
    );
  }
  return (
    <Button
      size="sm"
      variant="outline"
      disabled={radi || korisnik.archived_at !== null}
      title={`Korisnik ima ${reference.ukupno} ${reference.ukupno === 1 ? "zapis" : "zapisa"} u sustavu`}
      onClick={naArhiviranje}
    >
      Arhiviraj
    </Button>
  );
}

export function KorisniciPage() {
  const { can, user } = useAuth();
  const smije = can("auth", "read");
  const [korisnici, setKorisnici] = useState<Korisnik[] | null>(null);
  const [uloge, setUloge] = useState<Uloga[]>([]);
  const [radnici, setRadnici] = useState<RadnikZaPovezivanje[]>([]);
  const [obrazac, setObrazac] = useState<Obrazac | null>(null);
  const [pendingEmailChange, setPendingEmailChange] = useState<Obrazac | null>(null);
  const emailChangeDescriptionId = useId();
  const [lozinka, setLozinka] = useState<PrivremenaLozinka | null>(null);
  const [greska, setGreska] = useState<string | null>(null);
  const [radi, setRadi] = useState(false);
  const [prikaziArhivirane, setPrikaziArhivirane] = useState(false);
  // How many records each account has, once asked. Undefined until the answer
  // arrives, which is why a row offers neither button before then.
  const [reference, setReference] = useState<Record<string, Reference>>({});
  const smijeBrisati = can("auth", "delete");

  const ucitaj = useCallback(
    () =>
      Promise.all([
        dohvatiKorisnike(prikaziArhivirane),
        dohvatiUloge(),
        dohvatiRadnikeZaPovezivanje(),
      ])
        .then(([k, u, r]) => {
          setKorisnici(k);
          setUloge(u);
          setRadnici(r);
          if (!smijeBrisati) return;
          // One question per account, and only for whoever may act on the
          // answer. A failure leaves the row without buttons rather than
          // guessing that deleting is safe.
          for (const kandidat of k.filter((x) => !x.archived_at)) {
            void dohvatiReference(kandidat.id)
              .then((odgovor) =>
                setReference((prijasnje) => ({ ...prijasnje, [kandidat.id]: odgovor })),
              )
              .catch(() => undefined);
          }
        })
        .catch((error: unknown) => {
          setGreska(porukaDohvata(error));
          setKorisnici((postojeci) => postojeci ?? []);
        }),
    [prikaziArhivirane, smijeBrisati],
  );

  useEffect(() => {
    if (!smije) return;
    void ucitaj();
  }, [smije, ucitaj]);

  if (!smije) return <Navigate to="/" replace />;
  if (!korisnici) return <SkeletonTablica redaka={4} />;

  async function izvrsi(radnja: () => Promise<void>): Promise<void> {
    setRadi(true);
    setGreska(null);
    try {
      await radnja();
      await ucitaj();
    } catch (error: unknown) {
      setGreska(porukaSpremanja(error));
    } finally {
      setRadi(false);
    }
  }

  function spremi(event: FormEvent): void {
    event.preventDefault();
    if (!obrazac) return;
    const email = obrazac.email.trim().toLowerCase();
    if (obrazac.korisnik && email !== obrazac.korisnik.email.trim().toLowerCase()) {
      setPendingEmailChange({ ...obrazac, email });
      return;
    }
    saveForm(obrazac);
  }

  function saveForm(form: Obrazac, email?: string): void {
    const podaci = {
      full_name: form.full_name,
      telefon: form.telefon.trim() || null,
      uloge: form.uloge,
      radnik_id: form.radnik_id || null,
      ...(email ? { email } : {}),
    };
    void izvrsi(async () => {
      if (form.korisnik) {
        await izmijeniKorisnika(form.korisnik.id, podaci);
      } else {
        setLozinka(await stvoriKorisnika({ ...podaci, email: form.email }));
      }
      setObrazac(null);
    });
  }

  function resetiraj(korisnik: Korisnik): void {
    if (!window.confirm(`Resetirati lozinku za ${korisnik.full_name}? Sve prijave tog računa bit će prekinute.`)) return;
    void izvrsi(async () => setLozinka(await resetirajLozinku(korisnik.id)));
  }

  function promijeniAktivnost(korisnik: Korisnik): void {
    const pitanje = korisnik.is_active
      ? `Deaktivirati račun ${korisnik.full_name}? Prijava više neće biti moguća.`
      : `Ponovno aktivirati račun ${korisnik.full_name}?`;
    if (!window.confirm(pitanje)) return;
    void izvrsi(async () => {
      await postaviAktivnost(korisnik.id, !korisnik.is_active);
    });
  }

  function obrisi(korisnik: Korisnik): void {
    if (
      !window.confirm(
        `Trajno obrisati račun ${korisnik.email}? Račun nema zapisa u sustavu i ne može se vratiti.`,
      )
    )
      return;
    void izvrsi(async () => {
      await obrisiKorisnika(korisnik.id);
    });
  }

  function arhiviraj(korisnik: Korisnik): void {
    if (
      !window.confirm(
        `Arhivirati račun ${korisnik.email}? Prijava više neće biti moguća, a adresa se oslobađa za novi račun.`,
      )
    )
      return;
    void izvrsi(async () => {
      await arhivirajKorisnika(korisnik.id);
    });
  }

  function prebaciUlogu(kod: string): void {
    setObrazac((o) =>
      o && {
        ...o,
        uloge: o.uloge.includes(kod) ? o.uloge.filter((u) => u !== kod) : [...o.uloge, kod],
      },
    );
  }

  const slobodniRadnici = radnici.filter(
    (radnik) => !radnik.korisnik_id || radnik.korisnik_id === obrazac?.korisnik?.id,
  );

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Korisnici" meta="Računi za prijavu u aplikaciju. Što korisnik smije raditi određuju njegove uloge." actions={<>
<div className="flex flex-wrap gap-2">
          {smijeBrisati ? (
            <Button
              size="lg"
              variant="outline"
              aria-pressed={prikaziArhivirane}
              disabled={radi}
              onClick={() => setPrikaziArhivirane((trenutno) => !trenutno)}
            >
              {prikaziArhivirane ? "Sakrij arhivirane" : "Prikaži arhivirane"}
            </Button>
          ) : null}
          {can("auth", "create") && !obrazac ? (
            <Button size="lg" onClick={() => setObrazac(prazanObrazac())}>Dodaj korisnika</Button>
          ) : null}
        </div>
</>} />

      {lozinka ? (
        <section role="status" className={`${KARTICA} flex flex-col gap-3`}>
          <h2 className="text-[var(--text-card)] font-semibold">Privremena lozinka za {lozinka.korisnik.full_name}</h2>
          <p className="broj select-all rounded-[var(--radius-control)] bg-[var(--color-muted)] px-3 py-2 font-mono text-lg">{lozinka.privremena_lozinka}</p>
          <p className="text-[var(--color-muted-foreground)]">
            Prikazuje se samo sada. Predajte je korisniku ({lozinka.korisnik.email}); pri prvoj prijavi mora je promijeniti.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => void navigator.clipboard?.writeText(lozinka.privremena_lozinka)}>Kopiraj</Button>
            <Button variant="ghost" onClick={() => setLozinka(null)}>Zatvori</Button>
          </div>
        </section>
      ) : null}

      {greska ? <p role="alert" className="rounded-[var(--radius-card)] border border-[var(--color-status-bad-fg)]/25 bg-[var(--color-status-bad-bg)] p-4 text-[var(--color-status-bad-fg)]">{greska}</p> : null}

      {obrazac ? (
        <form onSubmit={spremi} className={`${KARTICA} flex flex-col gap-4`}>
          <h2 className="text-[var(--text-card)] font-semibold">{obrazac.korisnik ? `Uredi: ${obrazac.korisnik.email}` : "Novi korisnik"}</h2>
          <label className="flex flex-col gap-2">
            <span className="oznaka">Ime i prezime</span>
            <input required className={POLJE} value={obrazac.full_name} onChange={(e) => setObrazac({ ...obrazac, full_name: e.target.value })} />
          </label>
          <label className="flex flex-col gap-2">
            <span className="oznaka">Adresa e-pošte</span>
            <input required type="email" readOnly={!!obrazac.korisnik?.archived_at} className={POLJE} value={obrazac.email} onChange={(e) => setObrazac({ ...obrazac, email: e.target.value })} />
          </label>
          <label className="flex flex-col gap-2">
            <span className="oznaka">Kontakt telefon</span>
            <input type="tel" maxLength={30} className={POLJE} value={obrazac.telefon} onChange={(e) => setObrazac({ ...obrazac, telefon: e.target.value })} />
          </label>
          <fieldset className="flex flex-col gap-2">
            <legend className="oznaka mb-2">Uloge</legend>
            {uloge.map((uloga) => (
              <label key={uloga.code} className="flex min-h-11 items-start gap-3">
                <input type="checkbox" className="mt-1 size-5" checked={obrazac.uloge.includes(uloga.code)} onChange={() => prebaciUlogu(uloga.code)} />
                <span>
                  <span className="font-medium">{uloga.name_hr}</span>
                  {uloga.description ? <span className="block text-[var(--text-small)] text-[var(--color-muted-foreground)]">{uloga.description}</span> : null}
                </span>
              </label>
            ))}
          </fieldset>
          <label className="flex flex-col gap-2">
            <span className="oznaka">Povezani radnik (nije obvezno)</span>
            <DesignSelect aria-label="Povezani radnik" className={POLJE} value={obrazac.radnik_id} onChange={(e) => setObrazac({ ...obrazac, radnik_id: e.target.value })}>
              <option value="">— bez radnika —</option>
              {slobodniRadnici.map((radnik) => <option key={radnik.id} value={radnik.id}>{radnik.prezime} {radnik.ime}</option>)}
            </DesignSelect>
          </label>
          <div className="flex flex-wrap gap-2">
            <Button type="submit" size="lg" disabled={radi || obrazac.uloge.length === 0}>{radi ? "Spremam…" : "Spremi"}</Button>
            <Button type="button" variant="ghost" size="lg" onClick={() => setObrazac(null)}>Odustani</Button>
          </div>
        </form>
      ) : null}

      {pendingEmailChange?.korisnik ? (
        <Dialog
          title="Promjena adrese e-pošte"
          descriptionId={emailChangeDescriptionId}
          onClose={() => setPendingEmailChange(null)}
          actions={<>
            <Button type="button" variant="outline" onClick={() => setPendingEmailChange(null)}>Odustani</Button>
            <Button type="button" onClick={() => {
              saveForm(pendingEmailChange, pendingEmailChange.email);
              setPendingEmailChange(null);
            }}>Potvrdi promjenu</Button>
          </>}
        >
          <p id={emailChangeDescriptionId}>Promijeniti adresu za prijavu iz {pendingEmailChange.korisnik.email} u {pendingEmailChange.email}? Korisnik se sljedeći put prijavljuje novom adresom.</p>
        </Dialog>
      ) : null}

      {obrazac?.korisnik && can("auth", "mfa_manage") ? (
        <UserMfaSettings
          key={obrazac.korisnik.id}
          user={obrazac.korisnik}
          isSelf={obrazac.korisnik.id === user?.id}
          onUpdated={(updated) => {
            setKorisnici((current) => current?.map((item) => item.id === updated.id ? updated : item) ?? null);
            setObrazac((current) => current ? { ...current, korisnik: updated } : null);
          }}
        />
      ) : null}

      <section className="avm-surface overflow-x-auto rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)]">
        <table className="w-full min-w-[60rem] border-collapse text-left">
          <thead><tr className="border-b border-[var(--color-border)]"><th className="oznaka px-4 py-3">Ime i prezime</th><th className="oznaka px-4 py-3">Adresa e-pošte</th><th className="oznaka px-4 py-3">Uloge</th><th className="oznaka px-4 py-3">Radnik</th><th className="oznaka px-4 py-3">Status</th><th className="oznaka px-4 py-3">Zadnja prijava</th><th className="oznaka px-4 py-3">Radnje</th></tr></thead>
          <tbody>
            {korisnici.map((korisnik) => (
              <tr key={korisnik.id} className={korisnik.archived_at ? "border-b border-[var(--color-border)] align-top text-[var(--color-muted-foreground)] opacity-60" : "border-b border-[var(--color-border)] align-top last:border-b-0"}>
                <td className="px-4 py-4 font-medium">{korisnik.full_name}</td>
                <td className="px-4 py-4">{korisnik.archived_email ?? korisnik.email}</td>
                <td className="px-4 py-4">{korisnik.uloge.map((uloga) => uloga.name_hr).join(", ") || "—"}</td>
                <td className="px-4 py-4">{korisnik.radnik ? `${korisnik.radnik.prezime} ${korisnik.radnik.ime}` : "—"}</td>
                <td className="px-4 py-4">
                  {korisnik.archived_at ? (
                    <Badge ton="idle">Arhiviran {zadnjaPrijava(korisnik.archived_at)}</Badge>
                  ) : (
                    <>
                      <Badge ton={korisnik.is_active ? "ok" : "idle"}>{korisnik.is_active ? "Aktivan" : "Deaktiviran"}</Badge>
                      {korisnik.must_change_password ? <Badge ton="warn" className="mt-2">Čeka novu lozinku</Badge> : null}
                    </>
                  )}
                </td>
                <td className="broj px-4 py-4">{zadnjaPrijava(korisnik.zadnja_prijava)}</td>
                <td className="px-4 py-4">
                  {/* An archived account is a record, not an account: nothing
                      here applies to it any more. */}
                  {korisnik.archived_at ? <span className="text-[var(--text-small)]">—</span> : (
                  <div className="flex flex-wrap gap-2">
                    {can("auth", "update") ? <Button size="sm" variant="outline" disabled={radi} onClick={() => setObrazac(obrazacZa(korisnik))}>Uredi</Button> : null}
                    {can("auth", "update") && korisnik.id !== user?.id ? <Button size="sm" variant="outline" disabled={radi} onClick={() => resetiraj(korisnik)}>Postavi novu lozinku</Button> : null}
                    {korisnik.id !== user?.id && can("auth", korisnik.is_active ? "delete" : "update") ? <Button size="sm" variant={korisnik.is_active ? "destructive" : "outline"} disabled={radi} onClick={() => promijeniAktivnost(korisnik)}>{korisnik.is_active ? "Deaktiviraj" : "Aktiviraj"}</Button> : null}
                    <RadnjaRacuna
                      korisnik={korisnik}
                      reference={reference[korisnik.id]}
                      smije={smijeBrisati && korisnik.id !== user?.id}
                      radi={radi}
                      naBrisanje={() => obrisi(korisnik)}
                      naArhiviranje={() => arhiviraj(korisnik)}
                    />
                  </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
